import React, { ReactElement, useCallback, useEffect, useReducer, useState } from 'react';
import { QButton, QButtonGroup, QStepper, QStepperPanelActions } from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { UserAssociations } from '../../types';
import Steps from './Steps';
import { CommonProps } from './types';
import { AllEntitiesInMapReassigned } from './utils';
import { initializeState, reducer } from './state';
import SubmitHandler from './SubmitHandler';

type ReassignQualioEntitiesProps = Pick<CommonProps, 'users' | 'groupMap' | 'onSubmit'> & {
  itemsToReassign: UserAssociations;
  onCancel: () => void;
};

// Main Entry point for the stepper that can reassign Qualio Entities (docs and items) to new users
const ReassignQualioEntities = ({
  users,
  itemsToReassign,
  groupMap,
  onCancel,
  onSubmit,
}: ReassignQualioEntitiesProps): ReactElement => {
  const { t } = useTranslation();
  const [entityState, dispatch] = useReducer(reducer, itemsToReassign, initializeState);
  const [step, setStep] = useState(0);

  const [showNextButton, setShowNextButton] = useState(true);
  const [nextButtonEnabled, setNextButtonEnabled] = useState(false);
  const [removeButtonEnabled, setRemoveButtonEnabled] = useState(false);
  const [submitInProgress, setSubmitInProgress] = useState(false);

  const decrementStep = useCallback(() => setStep((stepNumber: number) => stepNumber - 1), []);
  const incrementStep = useCallback(() => setStep((stepNumber: number) => stepNumber + 1), []);

  const {
    owner: ownerItems,
    changeRequestOwner: changeRequestOwnerItems,
    approver: approverItems,
    reviewer: reviewerItems,
    issues: issueItems,
    tasks: taskItems,
    default_owner: defaultOwnerItems,
    eventTemplateOwner: eventTemplateOwnerItems,
    controls: controlItems,
  } = entityState;

  useEffect(() => {
    const removeButtonStatus =
      AllEntitiesInMapReassigned(ownerItems) &&
      AllEntitiesInMapReassigned(changeRequestOwnerItems) &&
      AllEntitiesInMapReassigned(approverItems) &&
      AllEntitiesInMapReassigned(reviewerItems) &&
      AllEntitiesInMapReassigned(issueItems) &&
      AllEntitiesInMapReassigned(taskItems) &&
      AllEntitiesInMapReassigned(defaultOwnerItems) &&
      AllEntitiesInMapReassigned(eventTemplateOwnerItems) &&
      AllEntitiesInMapReassigned(controlItems);

    setRemoveButtonEnabled(removeButtonStatus);
  }, [
    ownerItems,
    changeRequestOwnerItems,
    approverItems,
    reviewerItems,
    issueItems,
    taskItems,
    defaultOwnerItems,
    eventTemplateOwnerItems,
    controlItems,
  ]);

  return (
    <QStepper activeStep={step} onStepItemClicked={(stepNumber: number) => setStep(stepNumber)}>
      <SubmitHandler
        users={users}
        groupMap={groupMap}
        submitInProgress={submitInProgress}
        setSubmitInProgress={setSubmitInProgress}
        entityState={entityState}
        onSubmit={onSubmit}
      />
      <Steps
        users={users}
        setShowNextButton={setShowNextButton}
        setNextButtonEnabled={setNextButtonEnabled}
        incrementStep={incrementStep}
        entityState={entityState}
        dispatch={dispatch}
      />
      <QStepperPanelActions>
        <QButton data-cy="cancel-button" onClick={onCancel} variant="ghost">
          {t('reassignEntities.cancel')}
        </QButton>
        <QButton
          data-cy="previous-button"
          isDisabled={step === 0}
          onClick={decrementStep}
          leftIcon="ArrowLeft"
          variant="outline"
        >
          {t('reassignEntities.previous')}
        </QButton>
        <QButtonGroup>
          {showNextButton ? (
            <QButton
              data-cy="next-button"
              aria-label="next_button"
              isDisabled={!nextButtonEnabled}
              onClick={incrementStep}
              rightIcon="ArrowRight"
            >
              {t('reassignEntities.next')}
            </QButton>
          ) : (
            <QButton
              data-cy="remove-user-button"
              type="button"
              isDisabled={!removeButtonEnabled}
              isLoading={submitInProgress}
              isDestructive
              onClick={() => setSubmitInProgress(true)}
            >
              {t('reassignEntities.removeUser')}
            </QButton>
          )}
        </QButtonGroup>
      </QStepperPanelActions>
    </QStepper>
  );
};

export default ReassignQualioEntities;
